"use client";

import * as React from "react";
import { Message } from "@/lib/core/crm/types";
import { MessageActionHandlers } from "./hooks/useMessageActionHandlers";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@wtc/ui";
import { Button } from "@/components/ui/button";
import { MoreVertical, Trash2, CheckSquare, HardDrive, Paperclip, FileText } from "lucide-react";
import { ShareAttachmentsToSlackModal } from "./ShareAttachmentsToSlackModal";
import { ExportThreadToSlackModal } from "./ExportThreadToSlackModal";

interface MessageMoreActionsMenuProps {
  message: Message;
  handlers: MessageActionHandlers;
  onSaveAttachmentsToGoogleDrive?: () => void;
}

export function MessageMoreActionsMenu({
  message,
  handlers,
  onSaveAttachmentsToGoogleDrive,
}: MessageMoreActionsMenuProps) {
  const [shareAttachmentsOpen, setShareAttachmentsOpen] = React.useState(false);
  const [exportThreadOpen, setExportThreadOpen] = React.useState(false);

  // The "Share attachments to Slack" path downloads Outlook attachments
  // server-side through the connections seam (outlook-oauth). Gmail messages have no
  // attachment download path here, so the action is hidden for them.
  const canShareAttachmentsToSlack =
    message.hasAttachments && message.provider === "outlook-oauth";

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            title="More actions"
            disabled={handlers.isActionLoading}
          >
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {/* Create Task */}
          <DropdownMenuItem
            onClick={handlers.handleCreateTask}
            disabled={handlers.isActionLoading}
          >
            <CheckSquare className="h-4 w-4 mr-2" />
            <span>Create Task</span>
          </DropdownMenuItem>

          {/* Save Attachments to Google Drive */}
          {message.hasAttachments && onSaveAttachmentsToGoogleDrive && (
            <DropdownMenuItem
              onClick={onSaveAttachmentsToGoogleDrive}
              disabled={handlers.isActionLoading}
            >
              <HardDrive className="h-4 w-4 mr-2" />
              <span>Save Attachments to Drive</span>
            </DropdownMenuItem>
          )}

          {/* Share Attachments to Slack */}
          {canShareAttachmentsToSlack && (
            <DropdownMenuItem
              onClick={() => setShareAttachmentsOpen(true)}
              disabled={handlers.isActionLoading}
            >
              <Paperclip className="h-4 w-4 mr-2" />
              <span>Share attachments to Slack</span>
            </DropdownMenuItem>
          )}

          {/* Export Thread to Slack */}
          <DropdownMenuItem
            onClick={() => setExportThreadOpen(true)}
            disabled={handlers.isActionLoading}
          >
            <FileText className="h-4 w-4 mr-2" />
            <span>Export thread to Slack</span>
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          {/* Delete */}
          <DropdownMenuItem
            onClick={handlers.handleDelete}
            disabled={handlers.isActionLoading}
            className="text-destructive focus:text-destructive"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            <span>Delete</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {canShareAttachmentsToSlack && (
        <ShareAttachmentsToSlackModal
          open={shareAttachmentsOpen}
          onOpenChange={setShareAttachmentsOpen}
          message={message}
        />
      )}

      <ExportThreadToSlackModal
        open={exportThreadOpen}
        onOpenChange={setExportThreadOpen}
        message={message}
      />
    </>
  );
}
